import React from 'react'
import { useSelector } from 'react-redux'
import {
  ROUTE_SEO_ADDRESS,
  ROUTE_SEO_COUNTRY,
  ROUTE_SEO_DEPARTMENT,
  ROUTE_SEO_LANDMARK,
  ROUTE_SEO_NAMED_PLACE,
  ROUTE_SEO_REGION
} from '../../../routes'
import {
  isCountry,
  isDepartment,
  isDistrict,
  isLandmark,
  isNamedPlace,
  isRegion,
  isTown,
  isWay
} from '../../../store/address/address.helpers'
import { selectFirstAddress } from '../../../store/address/address.selectors'
import { selectLocale } from '../../../store/navigation/navigation.selectors'
import { selectBlockCanonical } from '../../../store/seo/seo.selectors'
import { compact } from '../../../utils/array'
import { isEmpty } from '../../../utils/lang'
import { slugify } from '../../../utils/string'
import { insertTranslationArgs } from '../../i18n/T'
import { generateFullRoute } from '../../router/routeUtils'
import MetaTags from '../../seo/MetaTags'
import { getSeoCityPostcode } from '../../seo/seo'
import { getImagePath } from './AddressImage.utils'
import mapping from './AddressMetaManager.json'

const buildMeta = ({ titleArgs, descriptionArgs, locale, canonical, image }) => {
  const translations = mapping[locale] ?? {}

  const title = !isEmpty(compact(titleArgs)) ? insertTranslationArgs(translations.title, titleArgs) : undefined

  const description = !isEmpty(compact(descriptionArgs))
    ? insertTranslationArgs(translations.description, descriptionArgs)
    : undefined

  return {
    title,
    description,
    canonical,
    image
  }
}

const generateCanonical = (route, postcode, label) =>
  postcode
    ? generateFullRoute(route, {
        location: postcode ? `${slugify(postcode, false)}-${slugify(label)}` : slugify(label)
      })
    : undefined

export const getMeta = (locale, address, blockCanonical) => {
  if (isEmpty(address)) return

  if (isCountry(address)) {
    const { countryCode, countryLabel } = address

    return {
      canonical: generateFullRoute(ROUTE_SEO_COUNTRY, {
        location: `${countryCode}-${slugify(countryLabel)}`
      })
    }
  } else if (isRegion(address)) {
    const { regionCode, regionLabel } = address

    return {
      canonical: generateFullRoute(ROUTE_SEO_REGION, { location: `${regionCode}-${slugify(regionLabel)}` })
    }
  } else if (isDepartment(address)) {
    const { departmentCode, departmentLabel } = address

    return {
      canonical: generateFullRoute(ROUTE_SEO_DEPARTMENT, {
        location: `${departmentCode}-${slugify(departmentLabel)}`
      })
    }
  } else if (isLandmark(address)) {
    const { name, postcode, label, town } = address

    const labelOrTown = name === label ? `${label}, ${postcode} ${town}` : label

    return buildMeta({
      locale,
      titleArgs: [name, labelOrTown],
      descriptionArgs: [name, label, name],
      image: getImagePath(address),
      canonical: !blockCanonical ? generateCanonical(ROUTE_SEO_LANDMARK, postcode, name) : undefined
    })
  } else if (isNamedPlace(address)) {
    const { named_place, postcode, label } = address

    return buildMeta({
      locale,
      titleArgs: [named_place, label],
      descriptionArgs: [named_place, label, named_place],
      image: getImagePath(address),
      canonical: !blockCanonical ? generateCanonical(ROUTE_SEO_NAMED_PLACE, postcode, named_place) : undefined
    })
  } else if (isDistrict(address)) {
    const { town, postcode } = address
    const townWithPostcode = `${town} (${postcode})`

    return buildMeta({
      locale,
      titleArgs: [town, townWithPostcode],
      descriptionArgs: [town, town, town],
      image: getImagePath(address),
      canonical: !blockCanonical ? generateCanonical(ROUTE_SEO_ADDRESS, postcode, town) : undefined
    })
  } else if (isTown(address)) {
    const { town, postcode: initialPostcode } = address
    const postcode = getSeoCityPostcode(initialPostcode)
    const townWithPostcode = postcode ? `${town} (${postcode})` : town

    return buildMeta({
      locale,
      titleArgs: [town, townWithPostcode],
      descriptionArgs: [town, town, town],
      image: getImagePath(address),
      canonical: !blockCanonical ? generateCanonical(ROUTE_SEO_ADDRESS, postcode, town) : undefined
    })
  } else if (isWay(address)) {
    const { named_place, name, town, label } = address
    const l = named_place ?? name ?? town

    return buildMeta({
      locale,
      titleArgs: [l, label],
      descriptionArgs: [l, label, l],
      image: undefined,
      canonical: undefined
    })
  } else {
    const { town, postcode, split_label } = address
    const townWithPostcode = postcode ? `${town} (${postcode})` : town

    return buildMeta({
      locale,
      titleArgs: [town, townWithPostcode],
      descriptionArgs: [town, town, town],
      canonical: !blockCanonical
        ? generateFullRoute(ROUTE_SEO_ADDRESS, {
            location: slugify(split_label?.join('-'))
          })
        : undefined
    })
  }
}

export const AddressMetaManager = () => {
  const address = useSelector(selectFirstAddress)
  const locale = useSelector(selectLocale)
  const blockCanonical = useSelector(selectBlockCanonical)

  return <MetaTags {...getMeta(locale, address, blockCanonical)} />
}

export default AddressMetaManager
